import { useCallback, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useVerifyAssessmentsUserMutation } from 'services/assessmentsService';
import { useAppDispatch } from 'store/store';
import {
  clearAssessments,
  setSaveCompleted,
  selectSaveAndCloseCompleted,
} from 'features/assessments/assessmentsSlice';
import { useSelector } from 'react-redux';
import { clearAssessmentsAuth } from 'features/assessments/assessmentsAuthSlice';
import { useAssessmentsAlertError } from './useAssessmentsAlertError';
import {
  isServerError,
  isClientError,
  isCredentialError,
  hasStatusCode,
} from '../utils/assessmentsErrorUtils';
import { transformVerifyUserRequest } from '../utils/apiDataTransformUtils';
import { useAssessmentsAuthConfig } from './useAssessmentsAuthConfig';
import { useAssessmentsParams } from './useAssessmentsParams';
import { setAssessmentsDetails } from '../assessmentsAuthSlice';
import { AssessmentStatus } from '../constants';

export const useAssessmentsAuth = () => {
  const { authenticationFields } = useAssessmentsAuthConfig();
  const { handleSubmit, ...formMethods } = useForm<FieldValues>();
  const { surveyEventId, tenantId } = useAssessmentsParams();
  const [verifyAssessmentsUser, { isLoading }] = useVerifyAssessmentsUserMutation();
  const { isShowingAlertError, alertError, showAlert } = useAssessmentsAlertError();
  const saveAndCloseCompleted = useSelector(selectSaveAndCloseCompleted);
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showNotFound, setShowNotFound] = useState<boolean>(false);

  const handleError = useCallback(
    (error: any) => {
      if (!hasStatusCode(error)) {
        showAlert('Please check your network and try again.');
        return;
      }

      const { status } = error;
      if (isServerError(status)) {
        showAlert('Something went wrong. Please try again later.');
      } else if (isClientError(status)) {
        setShowNotFound(true);
      } else if (isCredentialError(status)) {
        const backendMessage =
          (error as any).data?.message || 'Invalid credentials. Please try again.';
        setErrorMessage(backendMessage);
      }
    },
    [showAlert],
  );

  const onSubmit = useCallback(
    (fieldData: FieldValues) => {
      if (!tenantId || !surveyEventId) return;
      setErrorMessage('');
      setShowNotFound(false);
      verifyAssessmentsUser({
        tenantId,
        requestBody: transformVerifyUserRequest({
          authFields: authenticationFields ?? [],
          authdata: fieldData,
          surveyEventId,
        }),
      }).then(({ data, error }) => {
        if (error) {
          handleError(error);
        } else {
          dispatch(
            setAssessmentsDetails({
              accessToken: data?.accessToken ?? null,
              activityId: data?.activityId ?? null,
              assessmentsId: data?.assessmentsId ?? null,
              clientPatientId: data?.clientPatientId ?? null,
              firstName: data?.firstName ?? null,
            }),
          );
          if (data?.assessmentStatus === AssessmentStatus.completed) {
            dispatch(setSaveCompleted(true));
          }
        }
      });
    },
    [authenticationFields, surveyEventId, tenantId, verifyAssessmentsUser, dispatch, handleError],
  );

  const logoutAssessments = useCallback(() => {
    dispatch(clearAssessmentsAuth());
    dispatch(clearAssessments());
  }, [dispatch]);

  return {
    onSubmit: handleSubmit(onSubmit),
    formMethods,
    isSubmitting: isLoading,
    logoutAssessments,
    errorMessage,
    showNotFound,
    isShowingAlertError,
    alertError,
    saveAndCloseCompleted,
  };
};
